import React, { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Task } from "@common/types/task";
import { InvoiceViewSchema } from "@common/types/views/invoiceViews";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { StickyTaskMembershipPending } from "../shared/StickyTaskMembershipPending";
import { useStickyRefreshOnMount } from "../shared/useStickyRefreshOnMount";
import { useTaskTableViewState } from "../shared/useTaskTableViewState";
import { WORKSPACE_INVOICES_FILTER_TABS } from "./invoicesTaskFilters";
import { buildInvoicesTaskFilters } from "./invoicesTaskQuery";
import { InvoicesTaskTable } from "./InvoicesTaskTable";
import { parseInvoicesTaskView } from "./invoicesTaskView";
import styles from "./Invoices.module.scss";

interface Props {
    selectedTask: Task;
}

/**
 * Task body of an INVOICES task: the invoices table scoped by the task view.
 * Sticky tasks show their pinned membership and the creator's criteria as
 * locked pills; tasks created from a selection show exactly the selected
 * invoices.
 */
export const InvoicesTaskBody: React.FC<Props> = ({ selectedTask }) => {
    const queryClient = useQueryClient();
    const {
        taskView,
        sizedView,
        allowedColumnIds,
        handleViewChange,
        handleFiltersChange,
    } = useTaskTableViewState({
        selectedTask,
        viewSchema: InvoiceViewSchema,
        parseView: parseInvoicesTaskView,
    });
    const originalFilters = selectedTask.view?.originalFilters ?? undefined;

    const invalidateList = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: ["invoices"] });
    }, [queryClient]);

    const { isMembershipPending, resolvedMembership } = useStickyRefreshOnMount(
        { selectedTask, onMembershipAdded: invalidateList },
    );
    const stickyInvoiceIds =
        resolvedMembership?.invoiceIds ??
        selectedTask.view?.filters?.invoiceIds;

    const isPinned = !!originalFilters || !!taskView.filters.invoiceIds?.length;
    const availableFilters = isPinned ? [] : WORKSPACE_INVOICES_FILTER_TABS;
    const apiFilters = useMemo(
        () =>
            buildInvoicesTaskFilters({
                viewFilters: sizedView.filters,
                originalFilters,
                stickyInvoiceIds,
            }),
        [sizedView.filters, originalFilters, stickyInvoiceIds],
    );

    if (isMembershipPending) {
        return <StickyTaskMembershipPending />;
    }

    return (
        <div className={styles.root}>
            <InvoicesTaskTable
                view={sizedView}
                onViewChange={handleViewChange}
                onFiltersChange={handleFiltersChange}
                availableFilters={availableFilters}
                defaultView={taskView}
                apiFilters={apiFilters}
                allowedColumnIds={allowedColumnIds}
                originalFilters={originalFilters}
                className={styles.filters}
                tableClassName={styles.table}
                paginationType="loadMore"
                emptyView={() => (
                    <EmptyState
                        header={
                            isPinned
                                ? "No invoices associated with this task"
                                : "No invoices matching the task definition"
                        }
                    />
                )}
            />
        </div>
    );
};
